import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Calendar } from 'lucide-react';
import Header from '../../../components/ui/Header';
import StaffList from './components/StaffList';
import StaffForm from './components/StaffForm';
import AvailabilityScheduler from './components/AvailabilityScheduler';
import LeaveManagement from './components/LeaveManagement';
import { staffService } from '../../../services/staffService';

const StaffManagementDashboard = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    loadStaff();
  }, []);

  const loadStaff = async () => {
    try {
      setLoading(true);
      setError('');

      // Load both doctors and therapists (including inactive for admin management)
      const [doctors, therapists] = await Promise.all([
        staffService.getAllStaffByRole('doctor'),
        staffService.getAllStaffByRole('therapist')
      ]);

      setStaff([...doctors, ...therapists]);
    } catch (err) {
      setError('Failed to load staff. Please try again.');
      console.error('Error loading staff:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = () => {
    setSelectedStaff(null);
    setActiveTab('form');
  };

  const handleEditStaff = (staffMember) => {
    setSelectedStaff(staffMember);
    setActiveTab('form');
  };

  const handleManageAvailability = (staffMember) => {
    setSelectedStaff(staffMember);
    setActiveTab('availability');
  };

  const handleManageLeave = (staffMember) => {
    setSelectedStaff(staffMember);
    setActiveTab('leave');
  };

  const handleStaffSaved = () => {
    loadStaff();
    setActiveTab('list');
    setSelectedStaff(null);
  };

  const handleCancel = () => {
    setActiveTab('list');
    setSelectedStaff(null);
  };

  const filteredStaff = filterRole === 'all'
    ? staff
    : staff.filter(s => s.role === filterRole);

  const stats = [
    {
      label: 'Total Staff',
      value: staff.length,
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      label: 'Doctors',
      value: staff.filter(s => s.role === 'doctor').length,
      icon: Users,
      color: 'bg-purple-500'
    },
    {
      label: 'Therapists',
      value: staff.filter(s => s.role === 'therapist').length,
      icon: Users,
      color: 'bg-green-500'
    },
    {
      label: 'Active Today',
      value: staff.filter(s => s.isActive).length,
      icon: Calendar,
      color: 'bg-orange-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-20 pb-16">
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">Staff Management</h1>
            <p className="text-gray-600">Manage staff members and their availability schedules</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm mb-1">{stat.label}</p>
                    <p className="text-3xl font-bold text-gray-800">{stat.value}</p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="bg-white rounded-lg shadow-md mb-6">
            <div className="flex border-b">
              <button
                onClick={() => setActiveTab('list')}
                className={`px-6 py-4 font-medium transition-colors ${activeTab === 'list'
                  ? 'text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-purple-600'
                  }`}
              >
                <Users className="w-5 h-5 inline mr-2" />
                Staff List
              </button>
              {activeTab === 'form' && (
                <button
                  className="px-6 py-4 font-medium text-purple-600 border-b-2 border-purple-600"
                >
                  <UserPlus className="w-5 h-5 inline mr-2" />
                  {selectedStaff ? 'Edit Staff' : 'Add New Staff'}
                </button>
              )}
              {activeTab === 'availability' && (
                <button
                  className="px-6 py-4 font-medium text-purple-600 border-b-2 border-purple-600"
                >
                  <Calendar className="w-5 h-5 inline mr-2" />
                  Manage Availability
                </button>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="bg-white rounded-lg shadow-md p-6">
            {activeTab === 'list' && (
              <StaffList
                staff={filteredStaff}
                loading={loading}
                onCreateStaff={handleCreateStaff}
                onEditStaff={handleEditStaff}
                onManageAvailability={handleManageAvailability}
                onManageLeave={handleManageLeave}
                filterRole={filterRole}
                onFilterChange={setFilterRole}
                onStaffUpdated={loadStaff}
              />
            )}

            {activeTab === 'form' && (
              <StaffForm
                staff={selectedStaff}
                onSave={handleStaffSaved}
                onCancel={handleCancel}
              />
            )}

            {activeTab === 'availability' && (
              <AvailabilityScheduler
                staff={selectedStaff}
                onSave={handleStaffSaved}
                onCancel={handleCancel}
              />
            )}

            {activeTab === 'leave' && (
              <LeaveManagement
                staff={selectedStaff}
                onCancel={handleCancel}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffManagementDashboard;

